import { Action, createReducer, on } from "@ngrx/store";
import { CategoryDTO } from "src/app/models/Categories/categoryDTO";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import { ColumnSorterInfo } from "src/app/models/custom-table/custom-table.model";
import { SiteProductDTO } from "src/app/models/Sites/siteProductDTO";
import { SiteGradeDTO } from "src/app/models/Sites/siteGradeDTO";

import * as SitesActionTypes from "../../actions/sites/sites.actions";

import { SiteDTO } from "src/app/models/Sites/siteDTO";
import { SiteWarehouseDTO, WarehouseType } from "src/app/models/Sites/siteWarehouseDTO";
import { PumpType, SiteFuelPointDTO } from "src/app/models/Sites/siteFuelPointDTO";
import { SiteDispenserDTO } from "src/app/models/Sites/siteDispenserDTO";
import { FccType, SiteFccDTO } from "src/app/models/Sites/siteFccDTO";
import { PosType, SitePosDTO } from "src/app/models/Sites/sitePosDTO";
import { RegionDTO, SiteProfileDTO } from "src/app/models/Sites/siteCustomDTO";
import { PriceSignType, SitePriceSignDTO } from "src/app/models/Sites/sitePriceSignDTO";
import { PeripheralTypeDTO, SitePeripheralDTO } from "src/app/models/Sites/sitePeripheralDTO";
import { SiteMultimediaDTO } from "src/app/models/Sites/siteMultimediaDTO";

//Add DTOS TO SITES
export interface SitesState {
  sitesList: SiteDTO[];
  sitesExcelList: SiteDTO[];
  totalSites: number;
  isLoading: boolean;
  sitesTableConfig: ColumnSorterInfo[];
  enabledSiteList: SiteDTO[];
  created: boolean;
  site: SiteDTO;
  warehouses: SiteWarehouseDTO[];
  regions: RegionDTO[];
  profiles: SiteProfileDTO[];
  warehouseTypes: WarehouseType[];
  pumpTypes: PumpType[];
  priceSignTypes: PriceSignType[];
  posTypes: PosType[];
  error: Error;
  dispensers: SiteDispenserDTO[];
  fccs: SiteFccDTO[];
  pos: SitePosDTO[];
  fccDeviceSubTypes: FccType[];
  fuelPoints: SiteFuelPointDTO[];
  priceSigns: SitePriceSignDTO[];
  productList: SiteProductDTO[];
  fuelProductList: SiteProductDTO[];
  storeProductList: SiteProductDTO[];
  gradeList: SiteGradeDTO[];
  categoriesList: CategoryDTO[];
  propertiesList: PropertyDTO[];
  deviceWarehousePropertiesList: PropertyDTO[];
  devicePosPropertiesList: PropertyDTO[];
  peripherals: SitePeripheralDTO[];
  peripheralDeviceSubTypes: PeripheralTypeDTO[];
  multimediaDevices: SiteMultimediaDTO[];
}

export const initialState: SitesState = {
  sitesList: null,
  sitesExcelList: null,
  totalSites: 0,
  isLoading: false,
  sitesTableConfig: null,
  error: null,
  enabledSiteList: null,
  regions: null,
  profiles: null,
  warehouses: null,
  warehouseTypes: null,
  pumpTypes: null,
  priceSignTypes: null,
  posTypes: null,
  created: false,
  site: null,
  dispensers: null,
  fccs: null,
  pos: null,
  fccDeviceSubTypes: null,
  fuelPoints: null,
  priceSigns: null,
  productList: null,
  fuelProductList: null,
  storeProductList: null,
  gradeList: null,
  categoriesList: null,
  propertiesList: null,
  deviceWarehousePropertiesList: null,
  devicePosPropertiesList: null,
  peripherals: null,
  peripheralDeviceSubTypes: null,
  multimediaDevices: null,
};

const sitesReducer = createReducer(
  initialState,
  on(SitesActionTypes.resetSitesState, () => initialState),
  on(
    SitesActionTypes.getSitesList,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as SitesState)
  ),
  on(SitesActionTypes.loadSitesList, (state, { response }) => {
    return {
      ...state,
      sitesList: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
      totalSites: (response as any)?.result?.size
        ? (response as any)?.result?.size
        : 0,
    };
  }),
  on(
    SitesActionTypes.getSitesExcelList,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as SitesState)
  ),
  on(SitesActionTypes.loadSitesExcelList, (state, { response }) => {
    return {
      ...state,
      sitesExcelList: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    SitesActionTypes.getSitesTableConfig, 
    SitesActionTypes.getCategoriesForListByUsage, 
    SitesActionTypes.getPropertiesForListByUsage, 
    (state) => {
    return {
      ...state,
      isLoading: true,
    }
  }),
  on(SitesActionTypes.loadSitesTableConfig, (state, { response }) => {
    return {
      ...state,
      sitesTableConfig: (response as any)?.result?.value?.tableColumnInfo ?? undefined,
    };
  }),
  on(SitesActionTypes.saveSitesTableConfig, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(SitesActionTypes.loadCategoriesForListByUsage, (state, { response }) => {
    return {
      ...state,
      categoriesList: (response as any).result.items || [],
      error: null,
    };
  }),
  on(SitesActionTypes.loadPropertiesForListByUsage, (state, { response }) => {
    return {
      ...state,
      propertiesList: (response as any).result.items || [],
      error: null,
    };
  }),
  on(SitesActionTypes.loadDeviceWarehousePropertiesForListByUsage, (state, { response }) => {
    return {
      ...state,
      deviceWarehousePropertiesList: (response as any).result.items || [],
      error: null,
    };
  }),
  on(SitesActionTypes.loadDevicePosPropertiesForListByUsage, (state, { response }) => {
    return {
      ...state,
      devicePosPropertiesList: (response as any).result.items || [],
      error: null,
    };
  }),
  on(
    SitesActionTypes.getSiteProfileList,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
        isLoading: true,
      } as SitesState)
  ),
  on(SitesActionTypes.loadSiteProfileList, (state, { response }) => {
    return {
      ...state,
      profiles: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    SitesActionTypes.getSiteRegionsList,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
        isLoading: true,
      } as SitesState)
  ),
  on(SitesActionTypes.loadSiteRegionsList, (state, { response }) => {
    return {
      ...state,
      regions: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    SitesActionTypes.getFccDeviceSubtypesList,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
        isLoading: true,
      } as SitesState)
  ),
  on(SitesActionTypes.loadFccDeviceSubtypesList, (state, { response }) => {
    return {
      ...state,
      fccDeviceSubTypes: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    SitesActionTypes.getSite,
    (state, { code }) =>
      ({
        ...state,
        error: null,
        code: code,
        created: false,
      } as SitesState)
  ),
  on(SitesActionTypes.loadSite, (state, { response }) => {
    return {
      ...state,
      site: (response as any).result ? (response as any).result : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    SitesActionTypes.getSitePosList,
    (state, { code }) =>
      ({
        ...state,
        error: null,
        code: code,
      } as SitesState)
  ),
  on(SitesActionTypes.loadSitePosList, (state, { response }) => {
    return {
      ...state,
      pos: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    SitesActionTypes.getSiteFuelPointList,
    (state, { code }) =>
      ({
        ...state,
        error: null,
        code: code,
        isLoading: true,
      } as SitesState)
  ),
  on(SitesActionTypes.loadSiteFuelPointList, (state, { response }) => {
    return {
      ...state,
      fuelPoints: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    SitesActionTypes.getSitePriceSignList,
    (state, { code }) =>
      ({
        ...state,
        error: null,
        code: code,
        isLoading: true,
      } as SitesState)
  ),
  on(SitesActionTypes.loadSitePriceSignList, (state, { response }) => {
    return {
      ...state,
      priceSigns: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    SitesActionTypes.getSiteWarehouseList,
    (state, { code }) =>
      ({
        ...state,
        isLoading: true,
        error: null,
        code: code,
      } as SitesState)
  ),
  on(SitesActionTypes.loadSiteWarehouseList, (state, { response }) => {
    return {
      ...state,
      warehouses: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    SitesActionTypes.getSiteFccList,
    (state, { code }) =>
      ({
        ...state,
        error: null,
        code: code,
        isLoading: true,
      } as SitesState)
  ),
  on(SitesActionTypes.loadSiteFccList, (state, { response }) => {
    return {
      ...state,
      fccs: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    SitesActionTypes.getEnabledSitesList,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as SitesState)
  ),
  on(SitesActionTypes.loadEnabledSitesList, (state, { response }) => {
    return {
      ...state,
      enabledSiteList: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    SitesActionTypes.getWarehouseTypesList,
    (state, { pageReq }) =>
      ({
        ...state,
        isLoading: true,
        error: null,
        pageReq: pageReq,
      } as SitesState)
  ),
  on(SitesActionTypes.loadWarehouseTypesList, (state, { response }) => {
    return {
      ...state,
      warehouseTypes: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    SitesActionTypes.getPumpTypesList,
    SitesActionTypes.getPriceSignTypesList,
    SitesActionTypes.getPosTypesList,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
        isLoading: true,
      } as SitesState)
  ),
  on(SitesActionTypes.loadPumpTypesList, (state, { response }) => {
    return {
      ...state,
      pumpTypes: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(SitesActionTypes.loadPriceSignTypesList, (state, { response }) => {
    return {
      ...state,
      priceSignTypes: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(SitesActionTypes.loadPosTypesList, (state, { response }) => {
    return {
      ...state,
      posTypes: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    SitesActionTypes.getSiteDispenserList,
    (state, { code }) =>
      ({
        ...state,
        error: null,
        code: code,
        isLoading: true,
      } as SitesState)
  ),
  on(SitesActionTypes.loadSiteDispenserList, (state, { response }) => {
    return {
      ...state,
      dispensers: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    SitesActionTypes.getSiteProductList,
    SitesActionTypes.getSiteFuelProductList,
    SitesActionTypes.getSiteStoreProductList,
    SitesActionTypes.getSiteGradeList,
    (state, { code }) =>
      ({
        ...state,
        error: null,
        code: code,
        isLoading: true,
      } as SitesState)
  ),
  on(SitesActionTypes.loadSiteProductList, (state, { response }) => {
    return {
      ...state,
      productList: (response as any).result
        ? (response as any).result
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(SitesActionTypes.loadSiteFuelProductList, (state, { response }) => {
    return {
      ...state,
      fuelProductList: (response as any).result
        ? (response as any).result
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(SitesActionTypes.loadSiteStoreProductList, (state, { response }) => {
    return {
      ...state,
      storeProductList: (response as any).result
        ? (response as any).result
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(SitesActionTypes.loadSiteGradeList, (state, { response }) => {
    return {
      ...state,
      gradeList: (response as any).result
        ? (response as any).result
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(SitesActionTypes.loadSitePeripheralList, (state, { response }) => {
    return {
      ...state,
      peripherals: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    SitesActionTypes.getPeripheralDeviceSubtypesList,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
        isLoading: true,
      } as SitesState)
  ),
  on(SitesActionTypes.loadPeripheralDeviceSubtypesList, (state, { response }) => {
    return {
      ...state,
      peripheralDeviceSubTypes: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(SitesActionTypes.loadSiteMultimediaList, (state, { response }) => {
    return {
      ...state,
      multimediaDevices: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(SitesActionTypes.resetSite, (state) => {
    return {
      ...state,
      site: null,
      error: null,
    };
  }),
  on(SitesActionTypes.resetCreation, (state) => {
    return {
      ...state,
      created: true,
    };
  }),
  on(
    SitesActionTypes.createSite,
    SitesActionTypes.updateSite,
    SitesActionTypes.createWarehouse,
    SitesActionTypes.updateWarehouse,
    SitesActionTypes.createDispenser,
    SitesActionTypes.updateDispenser,
    SitesActionTypes.createFcc,
    SitesActionTypes.updateFcc,
    SitesActionTypes.createPos,
    SitesActionTypes.updatePos,
    SitesActionTypes.createFuelPoint,
    SitesActionTypes.updateFuelPoint,
    SitesActionTypes.createPriceSign,
    SitesActionTypes.updatePriceSign,
    SitesActionTypes.createPeripheral,
    SitesActionTypes.updatePeripheral,
    SitesActionTypes.createMultimedia,
    SitesActionTypes.updateMultimedia,
    SitesActionTypes.createProductList,
    SitesActionTypes.createFuelProductList,
    SitesActionTypes.createStoreProductList,
    SitesActionTypes.createGradeList,
    (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(SitesActionTypes.setLoadingEndState, (state) => {
    return {
      ...state,
      isLoading: false,
    };
  })
);

export function reducer(state: SitesState | undefined, action: Action) {
  return sitesReducer(state, action);
}
